import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import FileUpload from '../components/FileUpload';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { createApiUrl } from '../config/api';

function Profile() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { currentUser, authProvider } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [concentrations, setConcentrations] = useState([]);
  const [secondaryFields, setSecondaryFields] = useState([]);
  const [jointOptions, setJointOptions] = useState([]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [pasteModalOpen, setPasteModalOpen] = useState(false);
  const [coursePasteText, setCoursePasteText] = useState('');
  const [modalTab, setModalTab] = useState('paste'); // 'paste' or 'upload'
  
  const [profile, setProfile] = useState({
    concentration: '',
    secondary_field: '',
    academic_type: 'single', // 'single', 'joint', 'double'
    second_concentration: '', // for joint/double concentrations
    academic_interests: '', // free-text field for specializations
    year: '',
    courses_taken: [],
    interests: [],
    learning_preferences: []
  });
  
  // Form input state
  const [courseInput, setCourseInput] = useState('');
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [selectedPreferences, setSelectedPreferences] = useState([]);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        // First, fetch academic options (these should always work)
        const [concentrationsResponse, secondaryFieldsResponse] = await Promise.all([
          axios.get(createApiUrl('api/concentrations')),
          axios.get(createApiUrl('api/secondary-fields'))
        ]);
        
        setConcentrations(concentrationsResponse.data);
        setSecondaryFields(secondaryFieldsResponse.data);

        // Then try to fetch profile data (may not exist for new users)
        try {
          const profileResponse = await axios.get(createApiUrl('api/profile'));
          setProfile(profileResponse.data);
          
          if (profileResponse.data.interests) {
            setSelectedInterests(profileResponse.data.interests);
          }
          
          if (profileResponse.data.learning_preferences) {
            setSelectedPreferences(profileResponse.data.learning_preferences);
          }
        } catch (profileError) {
          // Profile doesn't exist yet - this is fine for new users
          console.log('No existing profile found - will create on first save');
        }

      } catch (error) {
        console.error('Error fetching academic data:', error);
        showToast('Failed to load academic options', 'error');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [showToast]);
  
  const handleChange = async (e) => {
    const { name, value } = e.target;
    setProfile({ ...profile, [name]: value });
    
    // When concentration changes, fetch joint options
    if (name === 'concentration' && value) {
      try {
        const jointOptionsResponse = await axios.get(createApiUrl(`api/joint-concentration-options/${encodeURIComponent(value)}`));
        
        setJointOptions(jointOptionsResponse.data);
        
        // Reset dependent fields when concentration changes
        setProfile(prev => ({
          ...prev,
          second_concentration: ''
        }));
      } catch (error) {
        console.error('Error fetching concentration options:', error);
      }
    }
  };
  
  // Fixed function to properly handle course input
  const handleAddCourse = (e) => {
    // Prevent default form submission behavior
    if (e) e.preventDefault();
    
    if (courseInput.trim()) {
      // Format course code to uppercase if it contains letters
      const formattedInput = courseInput.trim().replace(/([a-zA-Z]+)\s*(\d+[a-zA-Z]*)/i, (_, dept, num) => {
        return `${dept.toUpperCase()} ${num}`;
      });
      
      // Directly update the profile state with the new course
      const updatedCourses = [...profile.courses_taken];
      
      // Check if course already exists to avoid duplicates
      if (!updatedCourses.includes(formattedInput)) {
        updatedCourses.push(formattedInput);
        
        // Update profile with the new courses array
        setProfile({
          ...profile,
          courses_taken: updatedCourses
        });
        
        // Log for debugging
        console.log('Added course:', formattedInput);
        console.log('Updated courses:', updatedCourses);
      }
      
      // Clear the input field
      setCourseInput('');
    }
  };
  
  const handleRemoveCourse = (index) => {
    const updatedCourses = [...profile.courses_taken];
    updatedCourses.splice(index, 1);
    setProfile({ ...profile, courses_taken: updatedCourses });
  };
  
  const toggleInterest = (interest) => {
    const updatedInterests = selectedInterests.includes(interest)
      ? selectedInterests.filter(i => i !== interest)
      : [...selectedInterests, interest];
    
    setSelectedInterests(updatedInterests);
    setProfile({ ...profile, interests: updatedInterests });
  };
  
  const togglePreference = (preference) => {
    const updatedPreferences = selectedPreferences.includes(preference)
      ? selectedPreferences.filter(p => p !== preference)
      : [...selectedPreferences, preference];
    
    setSelectedPreferences(updatedPreferences);
    setProfile({ ...profile, learning_preferences: updatedPreferences });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await axios.post(createApiUrl('api/profile'), profile);
      showToast('Profile saved successfully', 'success');
      // Don't navigate away - let user stay on profile page
    } catch (error) {
      console.error('Error saving profile:', error);
      showToast('Failed to save profile', 'error');
    } finally {
      setSaving(false);
    }
  };
  
  const handlePdfUpload = async (file) => {
    setUploadLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('pdf', file);
      formData.append('provider', authProvider || 'openai'); // Send user's AI provider preference
      
      const token = localStorage.getItem('token');
      console.log('PDF Upload - Token exists:', !!token);
      console.log('PDF Upload - Token preview:', token ? token.substring(0, 20) + '...' : 'none');
      console.log('PDF Upload - Using provider:', authProvider || 'openai');
      
      const response = await axios.post(createApiUrl('api/extract_courses'), formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      
      const extractedCourses = response.data.courses || [];
      
      if (extractedCourses.length === 0) {
        showToast('No courses found in the PDF', 'warning');
      } else {
        // Combine existing courses with new ones, avoiding duplicates
        const existingCourseCodes = new Set(profile.courses_taken);
        const newCourses = extractedCourses.filter(course => !existingCourseCodes.has(course));
        
        setProfile({
          ...profile,
          courses_taken: [...profile.courses_taken, ...newCourses]
        });
        
        showToast(`Added ${newCourses.length} courses from PDF`, 'success');
        setPasteModalOpen(false);
      }
    } catch (error) {
      console.error('Error extracting courses from PDF:', error);
      
      // Handle the specific case where PDF extraction fails but suggests manual entry
      if (error.response?.status === 400 && error.response?.data?.fallback_available) {
        showToast(error.response.data.error || 'Could not extract courses from PDF. Try the paste option instead.', 'warning');
        // Auto-switch to paste tab for user convenience
        setModalTab('paste');
      } else {
        showToast('Failed to extract courses from PDF', 'error');
      }
    } finally {
      setUploadLoading(false);
    }
  };
  
  const handlePasteExtract = () => {
    if (!coursePasteText.trim()) {
      showToast('Please paste some text containing course codes', 'warning');
      return;
    }
    
    // Regular expression to match common course code formats
    // This regex looks for patterns like "CS 50", "MATH 21A", etc.
    const coursePattern = /([A-Za-z]{2,4})\s*(\d{1,3}[A-Za-z]{0,2})/g;
    const matches = [...coursePasteText.matchAll(coursePattern)];
    
    if (matches.length === 0) {
      showToast('No recognizable course codes found', 'warning');
      return;
    }
    
    // Format the matches and check for duplicates
    const existingCourseCodes = new Set(profile.courses_taken);
    const newCourses = [];
    
    matches.forEach(match => {
      const courseCode = `${match[1].toUpperCase()} ${match[2]}`;
      if (!existingCourseCodes.has(courseCode)) {
        newCourses.push(courseCode);
        existingCourseCodes.add(courseCode);
      }
    });
    
    if (newCourses.length === 0) {
      showToast('All found courses are already in your list', 'info');
    } else {
      setProfile({
        ...profile,
        courses_taken: [...profile.courses_taken, ...newCourses]
      });
      
      showToast(`Added ${newCourses.length} courses from text`, 'success');
      setCoursePasteText('');
      setPasteModalOpen(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex justify-center items-center transition-colors">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-slate-900 dark:border-slate-100"></div>
      </div>
    );
  }
  
  const interestOptions = [
    "Anthropology",
    "Art History",
    "Arts",
    "Biology",
    "Business",
    "Finance",
    "Chemistry",
    "Computer Science", 
    "Data Science",
    "Education",
    "Economics",
    "Environmental Studies",
    "Film and Visual Studies",
    "Gender Studies",
    "Government",
    "History",
    "International Relations",
    "Literature",
    "Mathematics",
    "Music",
    "Neuroscience",
    "Public Policy",
    "Philosophy",
    "Physics",
    "Psychology",
    "Public Health",
    "Sociology",
    "South Asian Studies"
  ];
  
  const learningOptions = [
    "Lectures", "Seminars", "Project-based", "Lab work", "Reading-intensive",
    "Writing-intensive", "Discussion-based", "Problem sets", "Research-oriented"
  ];
  
  const yearOptions = ["Freshman", "Sophomore", "Junior", "Senior"];
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-50 to-gray-100 dark:from-dark-100 dark:via-dark-200 dark:to-dark-100 font-sans antialiased">
      <Navbar />
      
      <div className="py-6 px-4 sm:px-6 lg:px-8 transform scale-90">
        <div className="max-w-4xl mx-auto">
          {/* Enterprise Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-light text-slate-900 dark:text-slate-100 mb-6 tracking-tight leading-tight">
              Hey <span className="text-accent-primary font-medium">{(currentUser?.firstName || (currentUser?.name ? currentUser.name.split(' ')[0] : (currentUser?.email ? currentUser.email.split('@')[0] : 'there'))).charAt(0).toUpperCase() + (currentUser?.firstName || (currentUser?.name ? currentUser.name.split(' ')[0] : (currentUser?.email ? currentUser.email.split('@')[0] : 'there'))).slice(1).toLowerCase()}</span>! Tell me a bit about you:
            </h1>
          </div>
          
          {/* Enterprise Form Container */}
          <div className="bg-white/80 dark:bg-dark-200/80 backdrop-blur-xl border border-slate-200/60 dark:border-dark-400/60 rounded-2xl shadow-lg overflow-hidden">
            <div className="px-8 py-10">
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                  {/* Concentration */}
                  <div className="sm:col-span-3">
                    <label htmlFor="concentration" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 tracking-tight">
                      Primary Concentration
                    </label>
                    <select
                      id="concentration"
                      name="concentration"
                      value={profile.concentration}
                      onChange={handleChange}
                      required
                      className="block w-full bg-white/80 dark:bg-dark-300/80 border-2 border-slate-200/60 dark:border-dark-400/60 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-accent-primary/20 focus:ring-offset-2 dark:focus:ring-accent-primary/20 dark:focus:ring-offset-dark-200 focus:border-accent-primary/40 dark:focus:border-accent-primary/40 text-sm transition-all duration-200 text-slate-900 dark:text-dark-700 tracking-tight leading-relaxed backdrop-blur-sm"
                    >
                      <option value="" className="text-slate-500">Select your concentration</option>
                      {concentrations.map((conc) => (
                        <option key={conc} value={conc}>{conc}</option>
                      ))}
                    </select>
                  </div>

                  {/* Academic Type */}
                  <div className="sm:col-span-3">
                    <label htmlFor="academic_type" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 tracking-tight">
                      Program Structure
                    </label>
                    <select
                      id="academic_type"
                      name="academic_type"
                      value={profile.academic_type}
                      onChange={handleChange}
                      className="block w-full bg-white/80 dark:bg-dark-300/80 border-2 border-slate-200/60 dark:border-dark-400/60 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-accent-primary/20 focus:ring-offset-2 dark:focus:ring-accent-primary/20 dark:focus:ring-offset-dark-200 focus:border-accent-primary/40 dark:focus:border-accent-primary/40 text-sm transition-all duration-200 text-slate-900 dark:text-dark-700 tracking-tight leading-relaxed backdrop-blur-sm"
                    >
                      <option value="single">Single Concentration</option>
                      <option value="joint">Joint Concentration</option>
                      <option value="double">Double Concentration</option>
                    </select>
                  </div>

                  {/* Second Concentration (for joint/double) */}
                  {(profile.academic_type === 'joint' || profile.academic_type === 'double') && (
                    <div className="sm:col-span-3">
                      <label htmlFor="second_concentration" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 tracking-tight">
                        {profile.academic_type === 'joint' ? 'Joint With' : 'Second Concentration'}
                      </label>
                      <select
                        id="second_concentration"
                        name="second_concentration"
                        value={profile.second_concentration}
                        onChange={handleChange}
                        className="block w-full bg-white/80 dark:bg-dark-300/80 border-2 border-slate-200/60 dark:border-dark-400/60 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-accent-primary/20 focus:ring-offset-2 dark:focus:ring-accent-primary/20 dark:focus:ring-offset-dark-200 focus:border-accent-primary/40 dark:focus:border-accent-primary/40 text-sm transition-all duration-200 text-slate-900 dark:text-dark-700 tracking-tight leading-relaxed backdrop-blur-sm"
                      >
                        <option value="" className="text-slate-500">Select {profile.academic_type === 'joint' ? 'joint partner' : 'second concentration'}</option>
                        {(profile.academic_type === 'joint' ? jointOptions : concentrations).map((conc) => (
                          <option key={conc} value={conc}>{conc}</option>
                        ))}
                      </select>
                    </div>
                  )}


                  {/* Secondary Field */}
                  <div className="sm:col-span-3">
                    <label htmlFor="secondary_field" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 tracking-tight">
                      Secondary Field <span className="text-xs text-slate-500 dark:text-slate-400 tracking-normal">(Optional)</span>
                    </label>
                    <select
                      id="secondary_field"
                      name="secondary_field"
                      value={profile.secondary_field}
                      onChange={handleChange}
                      className="block w-full bg-white/80 dark:bg-dark-300/80 border-2 border-slate-200/60 dark:border-dark-400/60 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-accent-primary/20 focus:ring-offset-2 dark:focus:ring-accent-primary/20 dark:focus:ring-offset-dark-200 focus:border-accent-primary/40 dark:focus:border-accent-primary/40 text-sm transition-all duration-200 text-slate-900 dark:text-dark-700 tracking-tight leading-relaxed backdrop-blur-sm"
                    >
                      <option value="" className="text-slate-500">Select secondary field</option>
                      {secondaryFields.map((field) => (
                        <option key={field} value={field}>{field}</option>
                      ))}
                    </select>
                  </div>

                  
                  {/* Year */}
                  <div className="sm:col-span-3">
                    <label htmlFor="year" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 tracking-tight">
                      Academic Year
                    </label>
                    <select
                      id="year"
                      name="year"
                      value={profile.year}
                      onChange={handleChange}
                      required
                      className="block w-full bg-white/80 dark:bg-dark-300/80 border-2 border-slate-200/60 dark:border-dark-400/60 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-accent-primary/20 focus:ring-offset-2 dark:focus:ring-accent-primary/20 dark:focus:ring-offset-dark-200 focus:border-accent-primary/40 dark:focus:border-accent-primary/40 text-sm transition-all duration-200 text-slate-900 dark:text-dark-700 tracking-tight leading-relaxed backdrop-blur-sm"
                    >
                      <option value="" className="text-slate-500">Select your year</option>
                      {yearOptions.map((year) => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Courses Taken */}
                  <div className="sm:col-span-6">
                    <div className="flex justify-between items-center mb-3">
                      <label htmlFor="courses" className="block text-sm font-medium text-slate-700 dark:text-slate-300 tracking-tight">
                        Courses You've Taken
                      </label>
                      <button
                        type="button"
                        onClick={() => setPasteModalOpen(true)}
                        className="text-sm text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 font-medium flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-all duration-200 border border-slate-200/60 dark:border-slate-700/60"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Import Courses
                      </button>
                    </div>
                    
                    {/* Course input */}
                    <div className="flex rounded-xl shadow-sm overflow-hidden">
                      <input
                        type="text"
                        name="course-input"
                        id="course-input"
                        value={courseInput}
                        onChange={(e) => setCourseInput(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddCourse();
                          }
                        }}
                        placeholder="e.g., MATH 55, CS 50"
                        className="flex-1 bg-white/80 dark:bg-dark-300/80 border-2 border-slate-200/60 dark:border-dark-400/60 border-r-0 rounded-l-xl py-3 px-4 focus:outline-none focus:ring-2 focus:ring-accent-primary/20 focus:ring-offset-2 dark:focus:ring-accent-primary/20 dark:focus:ring-offset-dark-200 focus:border-accent-primary/40 dark:focus:border-accent-primary/40 text-sm transition-all duration-200 text-slate-900 dark:text-dark-700 tracking-tight leading-relaxed backdrop-blur-sm placeholder-slate-400 dark:placeholder-dark-600"
                      />
                      <button
                        type="button"
                        onClick={handleAddCourse}
                        className="inline-flex items-center px-6 py-3 bg-accent-primary hover:bg-accent-tertiary text-white text-sm font-medium focus:outline-none focus:ring-2 focus:ring-accent-primary/20 focus:ring-offset-2 transition-all duration-200 rounded-r-xl border-2 border-accent-primary tracking-tight"
                      >
                        Add
                      </button>
                    </div>
                    
                    {/* Courses List */}
                    {profile.courses_taken.length > 0 && (
                      <div className="mt-4 p-4 bg-slate-50/80 dark:bg-dark-300/40 rounded-xl border border-slate-200/60 dark:border-dark-400/60 backdrop-blur-sm">
                        <div className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-3 tracking-tight">Your Courses:</div>
                        <div className="flex flex-wrap gap-2">
                          {profile.courses_taken.map((course, index) => (
                            <span key={index} className="inline-flex rounded-xl items-center py-2 pl-4 pr-2 text-sm font-medium bg-white dark:bg-dark-200 border border-slate-200/60 dark:border-dark-400/60 text-slate-800 dark:text-dark-700 shadow-sm backdrop-blur-sm">
                              {course}
                              <button
                                type="button"
                                onClick={() => handleRemoveCourse(index)}
                                className="flex-shrink-0 ml-2 h-5 w-5 rounded-full inline-flex items-center justify-center text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-600 dark:hover:text-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-900/20 dark:focus:ring-slate-100/20 transition-all duration-200"
                              >
                                <span className="sr-only">Remove {course}</span>
                                <svg className="h-3 w-3" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                                  <path strokeLinecap="round" strokeWidth="1.5" d="M1 1l6 6m0-6L1 7" />
                                </svg>
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Academic Interests */}
                  <div className="sm:col-span-6">
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 tracking-tight">
                      Academic Interests
                    </label>
                    <div className="bg-slate-50/80 dark:bg-dark-300/40 p-4 rounded-xl border border-slate-200/60 dark:border-dark-400/60 backdrop-blur-sm">
                      <div className="flex flex-wrap gap-2">
                        {interestOptions.map((interest) => (
                          <button
                            key={interest}
                            type="button"
                            onClick={() => toggleInterest(interest)}
                            className={`inline-flex items-center px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 tracking-tight
                              ${selectedInterests.includes(interest) 
                                ? 'bg-accent-primary text-white shadow-sm border-2 border-accent-primary' 
                                : 'bg-white dark:bg-dark-200 border-2 border-slate-200/60 dark:border-dark-400/60 text-slate-700 dark:text-dark-700 hover:bg-slate-50 dark:hover:bg-dark-300 hover:border-slate-300/60 dark:hover:border-dark-500/60 shadow-sm backdrop-blur-sm'
                              }`}
                          >
                            {interest}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Learning Preferences */}
                  <div className="sm:col-span-6">
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 tracking-tight">
                      Learning Preferences
                    </label>
                    <div className="bg-slate-50/80 dark:bg-dark-300/40 p-4 rounded-xl border border-slate-200/60 dark:border-dark-400/60 backdrop-blur-sm">
                      <div className="flex flex-wrap gap-2">
                        {learningOptions.map((preference) => (
                          <button
                            key={preference}
                            type="button"
                            onClick={() => togglePreference(preference)}
                            className={`inline-flex items-center px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 tracking-tight
                              ${selectedPreferences.includes(preference) 
                                ? 'bg-accent-primary text-white shadow-sm border-2 border-accent-primary' 
                                : 'bg-white dark:bg-dark-200 border-2 border-slate-200/60 dark:border-dark-400/60 text-slate-700 dark:text-dark-700 hover:bg-slate-50 dark:hover:bg-dark-300 hover:border-slate-300/60 dark:hover:border-dark-500/60 shadow-sm backdrop-blur-sm'
                              }`}
                          >
                            {preference}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Specific Academic Interests - Text Input */}
                  <div className="sm:col-span-6">
                    <label htmlFor="academic_interests" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 tracking-tight">
                      Specific Academic Interests <span className="text-xs text-slate-500 dark:text-slate-400 tracking-normal">(Optional)</span>
                    </label>
                    <input
                      type="text"
                      id="academic_interests"
                      name="academic_interests"
                      value={profile.academic_interests}
                      onChange={handleChange}
                      placeholder="e.g., distributed systems, artificial intelligence, international relations"
                      className="block w-full bg-white/80 dark:bg-slate-900/80 border-2 border-slate-200/60 dark:border-slate-700/60 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:ring-offset-2 dark:focus:ring-slate-100/20 dark:focus:ring-offset-slate-900 focus:border-slate-900/40 dark:focus:border-slate-100/40 text-sm transition-all duration-200 text-slate-900 dark:text-slate-100 tracking-tight leading-relaxed backdrop-blur-sm placeholder-slate-400 dark:placeholder-slate-500"
                    />
                    <p className="mt-2 text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                      Describe your specific academic interests or specializations
                    </p>
                  </div>
                </div>
                
                <div className="pt-10 flex justify-end">
                  <button
                    type="submit"
                    disabled={saving}
                    className="inline-flex items-center px-8 py-3 bg-accent-primary hover:bg-accent-tertiary text-white text-sm font-medium focus:outline-none focus:ring-2 focus:ring-accent-primary/20 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 rounded-xl shadow-sm tracking-tight leading-relaxed"
                  >
                    {saving ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving Profile...
                      </>
                    ) : (
                      'Save Academic Profile'
                    )}
                  </button>
                </div>
              </form>
            </div>
            <div className="bg-slate-50/60 dark:bg-dark-300/40 px-8 py-6 border-t border-slate-200/60 dark:border-dark-400/60 backdrop-blur-sm">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 mt-0.5">
                  <div className="w-8 h-8 bg-slate-100 dark:bg-dark-200 rounded-xl flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-slate-600 dark:text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                    The more details you provide, the better ChatHarvard can personalize course recommendations and academic advice. Your profile information helps our AI advisor understand your academic background and interests.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Course Import Modal */}
      {pasteModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-slate-900/20 dark:bg-slate-100/10"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl rounded-2xl text-left overflow-hidden shadow-2xl border border-slate-200/60 dark:border-slate-700/60 transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-xl font-light text-slate-900 dark:text-slate-100 mb-6 tracking-tight">
                      Import <span className="font-medium">Courses</span>
                    </h3>
                    
                    {/* Tabs */}
                    <div className="mt-4 border-b border-gray-200 dark:border-dark-400">
                      <div className="flex -mb-px">
                        <button
                          onClick={() => setModalTab('paste')}
                          className={`py-2 px-4 text-sm font-medium mr-4 transition-colors
                            ${modalTab === 'paste' 
                              ? 'border-b-2 border-accent-primary text-accent-primary dark:text-accent-secondary' 
                              : 'text-gray-500 dark:text-dark-600 hover:text-gray-700 dark:hover:text-dark-700 hover:border-gray-300 dark:hover:border-dark-500'
                            }`}
                        >
                          Paste Course List
                        </button>
                        <button
                          onClick={() => setModalTab('upload')}
                          className={`py-2 px-4 text-sm font-medium transition-colors
                            ${modalTab === 'upload' 
                              ? 'border-b-2 border-accent-primary text-accent-primary dark:text-accent-secondary' 
                              : 'text-gray-500 dark:text-dark-600 hover:text-gray-700 dark:hover:text-dark-700 hover:border-gray-300 dark:hover:border-dark-500'
                            }`}
                        >
                          Upload Transcript PDF
                        </button>
                      </div>
                    </div>
                    
                    <div className="mt-4">
                      {modalTab === 'paste' ? (
                        <div>
                          <label htmlFor="course-paste" className="block text-sm font-medium text-gray-700 dark:text-dark-700 mb-1">
                            Paste your course list from website, email, or document
                          </label>
                          <textarea
                            id="course-paste"
                            rows={5}
                            className="shadow-sm mt-1 block w-full sm:text-sm border border-gray-300 dark:border-dark-400 rounded-md p-2 bg-white dark:bg-dark-300 text-gray-900 dark:text-dark-700 focus:ring-accent-primary focus:border-accent-primary dark:focus:ring-accent-primary dark:focus:border-accent-primary"
                            value={coursePasteText}
                            onChange={(e) => setCoursePasteText(e.target.value)}
                            placeholder="Paste text containing course codes like CS 50, MATH 21A, etc."
                          />
                          <p className="mt-2 text-sm text-gray-500 dark:text-dark-600">
                            We'll automatically extract course codes from the text.
                          </p>
                        </div>
                      ) : (
                        <div>
                          <FileUpload
                            accept=".pdf"
                            onUpload={handlePdfUpload}
                            disabled={uploadLoading}
                            label="Upload Transcript PDF"
                            helperText="We'll extract your course codes from the PDF"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-slate-50/60 dark:bg-slate-900/40 px-6 py-4 sm:px-8 sm:py-5 sm:flex sm:flex-row-reverse gap-3 backdrop-blur-sm">
                {modalTab === 'paste' ? (
                  <button
                    type="button"
                    className="w-full inline-flex justify-center px-6 py-3 bg-accent-primary hover:bg-accent-tertiary text-white text-sm font-medium focus:outline-none focus:ring-2 focus:ring-accent-primary/20 focus:ring-offset-2 transition-all duration-200 rounded-xl shadow-sm tracking-tight sm:ml-3 sm:w-auto"
                    onClick={handlePasteExtract}
                  >
                    Extract Courses
                  </button>
                ) : (
                  uploadLoading && (
                    <div className="w-full sm:w-auto sm:ml-3 flex justify-center">
                      <div className="inline-flex items-center px-6 py-3 text-sm font-medium text-slate-600 dark:text-slate-400">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </div>
                    </div>
                  )
                )}
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center px-6 py-3 bg-white dark:bg-slate-800 border-2 border-slate-200/60 dark:border-slate-700/60 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:border-slate-300/60 dark:hover:border-slate-600/60 focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:ring-offset-2 dark:focus:ring-slate-100/20 dark:focus:ring-offset-slate-900 transition-all duration-200 rounded-xl shadow-sm tracking-tight backdrop-blur-sm sm:mt-0 sm:w-auto"
                  onClick={() => setPasteModalOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toggleable Disclaimer Info Icon - Bottom Left */}
      <div className="fixed bottom-4 left-4">
        <button
          onClick={() => setShowDisclaimer(!showDisclaimer)}
          className="w-10 h-10 bg-dark-200/95 backdrop-blur-xl border border-dark-400/60 rounded-full shadow-lg flex items-center justify-center hover:bg-dark-300/95 transition-all duration-200"
        >
          <svg className="w-5 h-5 text-dark-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </button>

        {showDisclaimer && (
          <div className="absolute bottom-12 left-0 max-w-md">
            <div className="bg-dark-200/95 backdrop-blur-xl border border-dark-400/60 rounded-xl shadow-lg p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <div className="w-6 h-6 bg-yellow-900/40 rounded-lg flex items-center justify-center">
                    <span className="text-yellow-400 font-bold text-xs">!</span>
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="text-xs font-medium text-dark-700 mb-2 tracking-tight">Important Disclaimer</h4>
                  <p className="text-xs text-dark-600 leading-relaxed mb-3">
                    Always confirm all results with your academic advisor and verify with academic advising reports on my.harvard.
                    This was developed by students for students. You should still use my.harvard and official channels for all academic decisions.
                  </p>

                  <h4 className="text-xs font-medium text-dark-700 mb-2 tracking-tight">Privacy & Data Use</h4>
                  <p className="text-xs text-dark-600 leading-relaxed">
                    We collect only course codes and academic preferences to provide personalized recommendations.
                    No transcripts, grades, or personally identifiable academic records are stored.
                    All data is encrypted and used solely to improve your academic planning experience.
                    We do not share your information with third parties.
                  </p>

                  <div className="mt-3 pt-2 border-t border-dark-400/40">
                    <p className="text-xs text-dark-500 italic">
                      For students, by students. Use responsibly alongside official academic resources.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Profile;